import { pushNotificationService } from './pushNotificationService'
import { getFcmVapidKey } from '../config/firebase-config'
import { getServiceWorkerConfig } from '../utils/serviceWorkerHelpers'
import { getFcmTokenWithSubscriptionRetry } from '../utils/pushSubscriptionHelpers'

const PUSH_TOKEN_KEY = 'fcm_token'
const PUSH_TOKEN_USER_KEY = 'fcm_token_user_id'
const DEVICE_ID_KEY = 'device_id'

function safeLocalStorageGet(key) {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeLocalStorageSet(key, value) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(key, value)
  } catch {
    // Ignore quota/privacy mode errors
  }
}

function safeLocalStorageRemove(key) {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(key)
  } catch {
    // Ignore quota/privacy mode errors
  }
}

function getUserId(user) {
  if (!user) return null
  return String(user.id_usuario || user.idUsuario || user.id || '')
}

function getDeviceId() {
  const existing = safeLocalStorageGet(DEVICE_ID_KEY)
  if (existing) return existing

  const newDeviceId = `web_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
  safeLocalStorageSet(DEVICE_ID_KEY, newDeviceId)
  return newDeviceId
}

function getDeviceInfo(token) {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
  const platform = typeof navigator !== 'undefined' ? navigator.platform || '' : ''
  const appVersionRaw = import.meta.env.VITE_APP_VERSION || '1.0.0'

  const appVersion = String(appVersionRaw).slice(0, 50)
  const deviceModel = String(userAgent).slice(0, 100)
  const osVersion = String(platform).slice(0, 50)

  return {
    token,
    platform: 'web',
    device_id: getDeviceId(),
    app_version: appVersion,
    device_model: deviceModel,
    os_version: osVersion
  }
}

async function getBrowserFcmToken() {
  const supportsPush =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'Notification' in window &&
    'PushManager' in window

  if (!supportsPush) return null
  if (Notification.permission !== 'granted') return null

  const { initializeApp, getApps } = await import('firebase/app')
  const { getMessaging, getToken } = await import('firebase/messaging')
  const firebaseConfigModule = await import('../config/firebase-config')

  const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfigModule.default)
  const messaging = getMessaging(app)
  const vapidKey = getFcmVapidKey()
  if (!vapidKey) return null

  const sw = getServiceWorkerConfig()
  await navigator.serviceWorker.register(sw.path, { scope: sw.scope })
  const registration = await navigator.serviceWorker.ready

  const token = await getFcmTokenWithSubscriptionRetry(getToken, messaging, {
    vapidKey,
    serviceWorkerRegistration: registration
  })

  return token || null
}

export function clearPushSessionStorage() {
  safeLocalStorageRemove(PUSH_TOKEN_KEY)
  safeLocalStorageRemove(PUSH_TOKEN_USER_KEY)
}

export function rememberPushTokenForUser(token, user) {
  if (!token) return
  safeLocalStorageSet(PUSH_TOKEN_KEY, token)
  const userId = getUserId(user)
  if (userId) {
    safeLocalStorageSet(PUSH_TOKEN_USER_KEY, userId)
  }
}

export function getStoredPushToken() {
  return safeLocalStorageGet(PUSH_TOKEN_KEY)
}

export async function unregisterPushForSession() {
  const token = getStoredPushToken()
  if (!token) {
    clearPushSessionStorage()
    return
  }

  try {
    await pushNotificationService.unregister(token)
  } catch (error) {
    console.warn('No se pudo desregistrar token push antes de logout:', error?.message || error)
  } finally {
    clearPushSessionStorage()
  }
}

export async function syncPushForAuthenticatedUser(user) {
  const userId = getUserId(user)
  if (!userId) return false

  // Solo sincronizar si el navegador ya otorgó permiso.
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false

  let browserToken = null
  try {
    browserToken = await getBrowserFcmToken()
  } catch (error) {
    console.warn('No se pudo obtener token FCM para sincronizar sesión:', error?.message || error)
    return false
  }

  if (!browserToken) return false

  const previousUserId = safeLocalStorageGet(PUSH_TOKEN_USER_KEY)
  const previousToken = safeLocalStorageGet(PUSH_TOKEN_KEY)

  if (previousToken && previousUserId && previousUserId !== userId) {
    try {
      await pushNotificationService.unregister(previousToken)
    } catch (error) {
      console.warn('No se pudo limpiar token de usuario previo:', error?.message || error)
    }
  }

  try {
    await pushNotificationService.register(getDeviceInfo(browserToken))
  } catch (error) {
    const backendError =
      error?.response?.data?.error ||
      error?.response?.data?.details ||
      error?.message ||
      'Error desconocido al registrar token push'
    console.warn('No se pudo registrar token push en backend:', backendError)
    return false
  }

  safeLocalStorageSet(PUSH_TOKEN_KEY, browserToken)
  safeLocalStorageSet(PUSH_TOKEN_USER_KEY, userId)

  return true
}

